import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import PageHeader from "src/components/common/PageHeader";
import Card from "src/components/common/card/Card";
import Input from "src/components/ui/Input";
import Textarea from "src/components/ui/Textarea";
import Dropdown from "src/components/ui/Dropdown";
import FilterSelect from "src/components/ui/FilterSelect";
import TableV2 from "src/components/ui/TableV2";
import Button from "src/components/ui/Button";
import SearchInput, { SearchInputOption } from "src/components/ui/SearchInput";
import ScanProductModal from "src/components/modals/ScanProductModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as adjustmentOrderActions from "src/redux/slices/adjustmentOrderSlice";
import * as warehouseActions from "src/redux/slices/warehouseSlice";
import useAssignedWarehouse from "src/hooks/useAssignedWarehouse";
import useWarehouseLabel from "src/hooks/useWarehouseLabel";
import { toast } from "react-toastify";
import productApis from "src/api/product";
import { AdjustmentOrderProduct } from "src/types/adjustmentOrder.types";
import { Product } from "src/types/product.types";
import { ScanLine, Trash } from "lucide-react";
import adjustmentOrderApis from "src/api/adjustmentOrder";

const AdjustmentForm: React.FC = () => {
  const { adjustmentId } = useParams();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const { create, edit } = useAppSelector((state) => state.adjustmentOrder);
  const { warehouseList } = useAppSelector((state) => state.warehouse);
  const { assignedWarehouseId, isWarehouseLocked } = useAssignedWarehouse();
  const { singular: warehouseLabel } = useWarehouseLabel();
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";

  // ------------------ State ------------------
  const [adjustList, setAdjustList] = useState<AdjustmentOrderProduct[]>([]);
  const [remarks, setRemarks] = useState("");
  const [warehouseId, setWarehouseId] = useState(assignedWarehouseId || "");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [highlightedProductId, setHighlightedProductId] = useState<string | null>(null);

  const warehouseOptions = (warehouseList?.data?.data || []).map((w: any) => ({
    label: w.name,
    value: w._id,
  }));

  useEffect(() => {
    dispatch(warehouseActions.getWarehouseListRequest({ page: 1, limit: 100 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isWarehouseLocked && assignedWarehouseId) setWarehouseId(assignedWarehouseId);
  }, [isWarehouseLocked, assignedWarehouseId]);

  useEffect(() => {
    const loadAdjustmentDetails = async () => {
      if (!adjustmentId) return;
      try {
        setIsLoadingDetails(true);
        const res = await adjustmentOrderApis.getDetails(adjustmentId);
        const order = res?.data;
        if (!order) {
          toast.error("Adjustment order not found");
          navigate("/app/adjustment/list");
          return;
        }
        const createdById =
          typeof order?.createdBy === "string"
            ? order.createdBy
            : order?.createdBy?._id;
        if (
          order?.orderStatus === "draft" &&
          !isTenantAdmin &&
          createdById &&
          user?._id &&
          String(createdById) !== String(user._id)
        ) {
          toast.error("Only the creator can edit this draft adjustment");
          navigate("/app/adjustment/list");
          return;
        }

        const mapped: AdjustmentOrderProduct[] =
          order.products?.map((item: any) => ({
            productId:
              typeof item.productId === "string"
                ? item.productId
                : item.productId?._id || "",
            name: item.productId?.name || item.name || "",
            sku: item.productId?.sku || item.sku,
            hsnCode: item.productId?.hsnCode || item.hsnCode,
            quantity: Number(item.quantity || 0),
            direction: item.direction || "in",
            reason: item.reason || "",
          })) || [];

        setAdjustList(mapped);
        setRemarks(order.remarks || "");
        setWarehouseId(
          typeof order.warehouseId === "string"
            ? order.warehouseId
            : order.warehouseId?._id || ""
        );

        if (order.orderStatus !== "draft") {
          toast.error("Only draft adjustments can be edited");
          navigate("/app/adjustment/list");
        }
      } catch (error) {
        console.error("Error fetching adjustment details:", error);
        toast.error("Failed to fetch adjustment details");
      } finally {
        setIsLoadingDetails(false);
      }
    };

    if (adjustmentId) {
      loadAdjustmentDetails();
    }
  }, [adjustmentId, navigate, isTenantAdmin, user?._id]);

  const handleClose = useCallback(() => {
    dispatch(adjustmentOrderActions.resetCreateAdjustmentOrder());
    dispatch(adjustmentOrderActions.resetEditAdjustmentOrder());
    navigate("/app/adjustment/list");
  }, [dispatch, navigate]);

  useEffect(() => {
    if (create.data) {
      toast.success("Stock adjustment created successfully!");
      handleClose();
    } else if (create.error) {
      toast.error(`Error: ${create.error}`);
    }
  }, [create, handleClose]);

  useEffect(() => {
    if (edit.data) {
      toast.success("Stock adjustment updated successfully!");
      handleClose();
    } else if (edit.error) {
      toast.error(`Error: ${edit.error}`);
    }
  }, [edit, handleClose]);

  // ------------------ Add / Update / Remove ------------------
  const handleAddProduct = (product: Product) => {
    const existingIdx = adjustList.findIndex((a) => a.productId === product._id);
    if (existingIdx !== -1) {
      const existing = adjustList[existingIdx];
      const updated = [...adjustList];
      updated[existingIdx] = {
        ...existing,
        quantity: (Number(existing.quantity) || 0) + 1,
      };
      setAdjustList(updated);
      toast.success(`Increased quantity for ${product.name}`);
      setHighlightedProductId(product._id || null);
      return;
    }

    setAdjustList([
      ...adjustList,
      {
        productId: product._id || "",
        name: product.name || "",
        sku: product.sku,
        hsnCode: product.hsnCode,
        quantity: 1,
        direction: "in",
        reason: "",
      },
    ]);
    toast.success(`Added ${product.name}`);
    setHighlightedProductId(product._id || null);
  };

  useEffect(() => {
    if (highlightedProductId === null) return;
    const timer = setTimeout(() => setHighlightedProductId(null), 1500);
    return () => clearTimeout(timer);
  }, [highlightedProductId]);

  const handleUpdateItem = (
    index: number,
    field: keyof AdjustmentOrderProduct,
    value: any
  ) => {
    const updated = [...adjustList];
    // @ts-ignore
    updated[index][field] = value;
    setAdjustList(updated);
  };

  const handleRemoveItem = (index: number) => {
    setAdjustList(adjustList.filter((_, i) => i !== index));
  };

  // ------------------ Submit ------------------
  const handleSubmit = (status: "draft" | "completed" = "completed") => {
    setSubmitAttempted(true);
    if (adjustList.length === 0) {
      toast.error("Please add at least one product adjustment");
      return;
    }
    if (!warehouseId) {
      toast.error(`Please select a ${warehouseLabel.toLowerCase()}`);
      return;
    }

    const payload = {
      warehouseId,
      products: adjustList.map((a) => ({
        productId: a.productId,
        name: a.name,
        quantity: Number(a.quantity),
        direction: a.direction,
        reason: a.reason,
      })),
      remarks,
      createdBy: user?._id || "",
      orderStatus: status,
    };

    if (adjustmentId) {
      dispatch(
        adjustmentOrderActions.editAdjustmentOrderRequest({
          id: adjustmentId,
          data: payload,
        })
      );
      return;
    }

    dispatch(adjustmentOrderActions.createAdjustmentOrderRequest(payload));
  };

  const pageTitle = adjustmentId ? "Edit Adjustment" : "Create Adjustment";

  return (
    <DashboardLayout
      meta={{
        title: adjustmentId
          ? "Edit Adjustment | Invyntra"
          : "Create Adjustment | Invyntra",
        description: "Create stock adjustments.",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title=""
          showBack={!!adjustmentId}
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Adjustments", path: "/app/adjustment/list" },
            { label: pageTitle, path: "" },
          ]}
        />

        <Card>
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-800 text-center">
              {pageTitle}
            </h2>

            {/* ---------------- WAREHOUSE ---------------- */}
            <div className="border rounded-lg p-4 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60">
              <FilterSelect
                variant="boxed"
                label={warehouseLabel}
                required
                value={warehouseId}
                onChange={setWarehouseId}
                allLabel="Select"
                options={warehouseOptions}
                error={submitAttempted && !warehouseId}
                disabled={isWarehouseLocked}
                helperText={
                  isWarehouseLocked
                    ? `Locked to your assigned ${warehouseLabel.toLowerCase()}.`
                    : `Stock changes below apply at this ${warehouseLabel.toLowerCase()}.`
                }
              />
            </div>

            {/* ---------------- PRODUCTS ---------------- */}
            <div className="border rounded-lg p-4 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="h-4 w-1 rounded-full bg-primary-500" />
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Products
                  </h3>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    {adjustList.length}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="Scan barcode"
                  onClick={() => setScanOpen(true)}
                >
                  <ScanLine size={14} />
                </Button>
              </div>

              <SearchInput<Product>
                label="Add Product"
                placeholder="Search by name, SKU, HSN, or product ID..."
                clearOnSelect
                onSearch={async (query) => {
                  const res = await productApis.getProductList({
                    page: 1,
                    limit: 10,
                    status: "active",
                    search: query,
                  });
                  const list: Product[] = res?.data?.data || res?.data || [];
                  return list.map((p) => ({
                    value: p._id || "",
                    label: `${p.name} (${p.sku || p.productId})`,
                    data: p,
                  }));
                }}
                onSelect={(option: SearchInputOption<Product>) =>
                  handleAddProduct(option.data)
                }
                renderOption={(option) => (
                  <div>
                    <div className="font-medium text-gray-800">
                      {option.data.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      SKU: {option.data.sku || "-"} · HSN:{" "}
                      {option.data.hsnCode || "-"} · ID:{" "}
                      {option.data.productId}
                    </div>
                  </div>
                )}
              />

              {adjustList.length > 0 && (
                <div className="mt-4">
                  <TableV2
                    columns={[
                      {
                        key: "name",
                        header: "Product",
                        accessor: ({ row }) => (
                          <div>
                            <div className="font-medium text-gray-800">
                              {row.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              SKU: {row.sku || "-"} · HSN: {row.hsnCode || "-"}
                            </div>
                          </div>
                        ),
                      },
                      {
                        key: "quantity",
                        header: "Qty",
                        width: "90px",
                        accessor: ({ row, rowIndex }) => (
                          <Input
                            type="number"
                            inputClassName="text-center"
                            value={row.quantity}
                            onChange={(e) =>
                              handleUpdateItem(
                                rowIndex,
                                "quantity",
                                e.target.value
                              )
                            }
                          />
                        ),
                      },
                      {
                        key: "direction",
                        header: "Direction",
                        width: "160px",
                        accessor: ({ row, rowIndex }) => (
                          <Dropdown
                            value={row.direction}
                            options={[
                              { label: "Add Stock", value: "in" },
                              { label: "Remove Stock", value: "out" },
                            ]}
                            onChange={(val) =>
                              handleUpdateItem(rowIndex, "direction", val)
                            }
                            fullWidth
                          />
                        ),
                      },
                      {
                        key: "reason",
                        header: "Reason",
                        accessor: ({ row, rowIndex }) => (
                          <Input
                            type="text"
                            placeholder="e.g. Damaged, Expired, Correction"
                            value={row.reason || ""}
                            onChange={(e) =>
                              handleUpdateItem(
                                rowIndex,
                                "reason",
                                e.target.value
                              )
                            }
                          />
                        ),
                      },
                      {
                        key: "action",
                        header: "",
                        width: "48px",
                        accessor: ({ rowIndex }) => (
                          <button
                            onClick={() => handleRemoveItem(rowIndex)}
                            className="hover:bg-red-100 text-red-600 px-2 py-2 rounded-md"
                          >
                            <Trash size={16} />
                          </button>
                        ),
                      },
                    ]}
                    data={adjustList}
                    rowClassName={(row) =>
                      row.productId === highlightedProductId
                        ? "!bg-primary-50/60 dark:!bg-primary-900/20 ring-2 ring-inset ring-primary-300 dark:ring-primary-700"
                        : ""
                    }
                  />
                </div>
              )}
            </div>

            {/* ---------------- REMARKS ---------------- */}
            <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
              <Textarea
                label="Remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Additional notes..."
              />
            </div>

            {/* ---------------- ACTION BUTTONS ---------------- */}
            <div className="flex justify-end space-x-3 pb-2">
              <Button onClick={handleClose} variant="outline">
                Cancel
              </Button>
              <Button
                onClick={() => handleSubmit("draft")}
                variant="outline"
                isLoading={create.loading || edit.loading || isLoadingDetails}
                disabled={!adjustList.length || isLoadingDetails}
              >
                Save as Draft
              </Button>
              <Button
                onClick={() => handleSubmit("completed")}
                isLoading={create.loading || edit.loading || isLoadingDetails}
                disabled={!adjustList.length || isLoadingDetails}
                className="bg-indigo-600 text-white"
              >
                Place Order
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <ScanProductModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onProductFound={(product) => handleAddProduct(product)}
      />
    </DashboardLayout>
  );
};

export default AdjustmentForm;
